import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const DRAG_RESUME_DELAY = 1500; // ms before resuming animation after drag
const MOMENTUM_FRICTION = 0.95; // Friction factor for momentum (0-1, higher = less friction)
const MOMENTUM_MIN_VELOCITY = 0.5; // Minimum velocity to stop momentum
const VELOCITY_SAMPLE_COUNT = 5; // Number of samples to average for velocity

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragOffset = 0;

  /** @type {number} */
  #currentTranslateX = 0;

  /** @type {number | null} */
  #resumeTimeout = null;

  /** @type {number | null} */
  #momentumAnimationId = null;

  /** @type {Array<{x: number, time: number}>} */
  #velocitySamples = [];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    // Drag functionality
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    this.addEventListener('pointerleave', this.#handlePointerUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('pointerleave', this.#handlePointerUp);

    if (this.#resumeTimeout) {
      clearTimeout(this.#resumeTimeout);
    }

    if (this.#momentumAnimationId) {
      cancelAnimationFrame(this.#momentumAnimationId);
    }
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    // Don't slow down during drag or when resuming from drag
    if (this.#animation || this.#isDragging || this.#resumeTimeout) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    // Don't speed up during drag or when resuming from drag
    if (this.#isDragging || this.#resumeTimeout) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  /**
   * SHOP-30: clones stay visual-only. Hide them from AT and demote cloned
   * paragraphs to spans so the same <p> text is not indexed twice.
   * @param {HTMLElement} clone
   */
  #seoSanitizeClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('p').forEach((paragraph) => {
      const span = document.createElement('span');
      span.className = paragraph.className;
      span.replaceChildren(...paragraph.childNodes);
      paragraph.replaceWith(span);
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.#seoSanitizeClone(clone);

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (wrapper.cloneNode(true));
      this.#seoSanitizeClone(clone);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }

  /**
   * Handle pointer down - start dragging
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    // Only handle primary button (left click / touch)
    if (event.button !== 0) return;

    // Prevent text selection and other default behaviors
    event.preventDefault();

    // Cancel any pending resume first
    if (this.#resumeTimeout) {
      clearTimeout(this.#resumeTimeout);
      this.#resumeTimeout = null;
    }

    // Cancel any ongoing momentum animation
    if (this.#momentumAnimationId) {
      cancelAnimationFrame(this.#momentumAnimationId);
      this.#momentumAnimationId = null;
    }

    // Get current animation position and pause BEFORE setting drag state
    this.#pauseAnimationAtCurrentPosition();

    // Reset velocity tracking
    this.#velocitySamples = [{ x: event.clientX, time: performance.now() }];

    // Now set drag state
    this.#isDragging = true;
    this.#dragStartX = event.clientX;
    this.setPointerCapture(event.pointerId);

    // Set cursor style
    this.style.cursor = 'grabbing';
    this.style.userSelect = 'none';
  };

  /**
   * Handle pointer move - drag the marquee
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (!this.#isDragging) return;

    const deltaX = event.clientX - this.#dragStartX;
    const newTranslateX = this.#currentTranslateX + deltaX;

    this.refs.wrapper.style.transform = `translate3d(${newTranslateX}px, 0, 0)`;
    this.#dragOffset = deltaX;

    // Track velocity samples
    const now = performance.now();
    this.#velocitySamples.push({ x: event.clientX, time: now });

    // Keep only recent samples
    while (this.#velocitySamples.length > VELOCITY_SAMPLE_COUNT) {
      this.#velocitySamples.shift();
    }
  };

  /**
   * Handle pointer up - stop dragging and resume animation after delay
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.releasePointerCapture(event.pointerId);

    // Update current position
    this.#currentTranslateX += this.#dragOffset;
    this.#dragOffset = 0;

    // Reset cursor
    this.style.cursor = '';
    this.style.userSelect = '';

    // Calculate velocity from samples
    const velocity = this.#calculateVelocity();

    // If there's significant velocity, apply momentum
    if (Math.abs(velocity) > MOMENTUM_MIN_VELOCITY) {
      this.#applyMomentum(velocity);
    } else {
      // No momentum, just normalize and schedule resume
      this.#normalizePosition();
      this.#scheduleResume();
    }
  };

  /**
   * Calculate velocity from recent samples
   * @returns {number} Velocity in pixels per frame (roughly 16ms)
   */
  #calculateVelocity() {
    if (this.#velocitySamples.length < 2) return 0;

    const oldest = this.#velocitySamples[0];
    const newest = this.#velocitySamples[this.#velocitySamples.length - 1];
    const timeDelta = newest.time - oldest.time;

    if (timeDelta === 0) return 0;

    // Calculate velocity in pixels per millisecond, then convert to pixels per frame (~16ms)
    const velocityPerMs = (newest.x - oldest.x) / timeDelta;
    return velocityPerMs * 16;
  }

  /**
   * Apply momentum animation after drag release
   * @param {number} initialVelocity - Starting velocity in pixels per frame
   */
  #applyMomentum(initialVelocity) {
    let velocity = initialVelocity;

    const animate = () => {
      // Apply velocity to position
      this.#currentTranslateX += velocity;
      this.refs.wrapper.style.transform = `translate3d(${this.#currentTranslateX}px, 0, 0)`;

      // Apply friction
      velocity *= MOMENTUM_FRICTION;

      // Continue if velocity is still significant
      if (Math.abs(velocity) > MOMENTUM_MIN_VELOCITY) {
        this.#momentumAnimationId = requestAnimationFrame(animate);
      } else {
        // Momentum finished
        this.#momentumAnimationId = null;
        this.#normalizePosition();
        this.#scheduleResume();
      }
    };

    this.#momentumAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Schedule the animation resume after delay
   */
  #scheduleResume() {
    this.#resumeTimeout = window.setTimeout(() => {
      this.#resumeAnimation();
      this.#resumeTimeout = null;
    }, DRAG_RESUME_DELAY);
  }

  /**
   * Pause the CSS animation and capture current position
   */
  #pauseAnimationAtCurrentPosition() {
    const { wrapper } = this.refs;
    const animation = wrapper.getAnimations()[0];

    // Always get current position from computed style first
    const computedStyle = getComputedStyle(wrapper);
    const matrix = new DOMMatrix(computedStyle.transform);
    const currentPosition = matrix.m41;

    // Only update if we got a valid position (not identity matrix)
    if (computedStyle.transform !== 'none') {
      this.#currentTranslateX = currentPosition;
    }

    if (animation) {
      // Cancel the animation completely to avoid conflicts
      animation.cancel();
    }

    // Disable the CSS animation property
    wrapper.style.animation = 'none';

    // Apply our position as inline style
    wrapper.style.transform = `translate3d(${this.#currentTranslateX}px, 0, 0)`;
  }

  /**
   * Normalize position to keep it within a reasonable range for seamless looping
   */
  #normalizePosition() {
    const { wrapper, content } = this.refs;
    const contentWidth = content.offsetWidth;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 0;
    const totalWidth = contentWidth + gap;

    // Keep position within one cycle
    if (totalWidth > 0) {
      while (this.#currentTranslateX > 0) {
        this.#currentTranslateX -= totalWidth;
      }
      while (this.#currentTranslateX < -totalWidth) {
        this.#currentTranslateX += totalWidth;
      }
    }

    wrapper.style.transform = `translate3d(${this.#currentTranslateX}px, 0, 0)`;
  }

  /**
   * Resume the animation from current position
   */
  #resumeAnimation() {
    const { wrapper } = this.refs;

    // Calculate the animation range
    // The wrapper contains content twice, animation moves by -50% - gap/2
    // which equals one full content width + gap
    const contentWidth = this.refs.content.offsetWidth;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 0;
    const animationRange = contentWidth + gap;

    // Normalize position to be within one animation cycle [0, -animationRange]
    let normalizedPosition = this.#currentTranslateX;
    while (normalizedPosition > 0) {
      normalizedPosition -= animationRange;
    }
    while (normalizedPosition < -animationRange) {
      normalizedPosition += animationRange;
    }

    // Calculate progress (0 to 1) through the animation cycle
    const progress = Math.abs(normalizedPosition / animationRange);

    // Re-enable the CSS animation (paused initially)
    wrapper.style.animation = '';

    // Force reflow to restart animation
    wrapper.offsetHeight;

    // Get the new animation
    const animation = wrapper.getAnimations()[0];
    if (animation) {
      // Pause immediately
      animation.pause();

      // Set the animation to the correct position
      const duration = animation.effect?.getComputedTiming().duration;
      if (typeof duration === 'number') {
        animation.currentTime = progress * duration;
      }

      // Now remove our inline transform - the paused animation will hold the position
      wrapper.style.transform = '';

      // Force reflow
      wrapper.offsetHeight;

      // Smoothly resume by animating the playback rate
      animation.updatePlaybackRate(0);
      animation.play();

      // Gradually increase playback rate to 1
      animateValue({
        from: 0,
        to: 1,
        duration: 300,
        onUpdate: (value) => animation.updatePlaybackRate(value),
      });
    } else {
      // Fallback: just clear the styles
      wrapper.style.transform = '';
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
